import { useEffect, useRef, useState, useMemo } from "react";
import { allowOrWarn, Util } from '../../util/Util';
import SupplyReport from "../supply/SupplyReport";
import SupplierPaymentModal from "./SupplierPaymentModal";
import SupplierBulkPaymentModal from "./SupplierBulkPaymentModal";
import { useData } from "../../hooks/useData";
import { useUI } from "../../providers/MoleUIContext";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useDateRange, periodRequestConfig } from "../../hooks/useDateRange";
import { Box, IconButton, ListItemIcon, MenuItem, Tooltip } from "@mui/material";
import { LocalStorageKeys, NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from "../../util/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AddCircleOutline, Delete, DeleteOutline, Edit, Refresh } from "@mui/icons-material";
import TableView from "../../components/TableView";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MRT_ColumnDef } from "material-react-table";
import ExportMenuButton from "../../components/ExportMenuButton";
import { createSignCell } from "../../util/cellFormatters";
import { useAppSocket } from "../../providers/AppSocketContext";
import { parseISODay } from "../../util/businessClock";

import { roundQty } from "../../util/quantity";
import { canAmend, canDrop, canRecord } from '../../util/access';
interface Props {
	bundle?: any;
	hideColumns?: string[];
	onCallback?: () => void;
}

const SupplyPayment = ({ bundle, hideColumns, onCallback }: Props) => {
	const [toEdit, setToEdit] = useState<any>(null);
	const [toDelete, setToDelete] = useState<any>(null);
	const [toDeleteBulk, setToDeleteBulk] = useState<any[]>([]);
	const [toView, setToView] = useState<any>(null);
	const [newRecord, setNewRecord] = useState<boolean>(false);

	const { api, session, access } = useAuth();
	const { appSettings } = useAppSocket();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { data, lastUpdate, loadFromIndexedDB, saveFromServer, dateRange, loading, setLoading, setData } =
		useData(LocalStorageKeys.SUPPLY_PAYMENT_RECORDS);
	const { dateRange: period, setDateRange: setPeriod } = useDateRange(bundle ? undefined : LocalStorageKeys.SUPPLY_PAYMENT_RECORDS);

	const paymentMade = useRef(false);
	const toastIdRef = useRef<string | number>(-1);
	const tableRef = useRef<any>(null);
	const firstFetchRef = useRef(false);

	useEffect(() => {
		return () => {
			if (paymentMade.current) {
				onCallback?.();
			}
		};
	}, [onCallback]);

	useEffect(() => {
		if (!bundle && !loadFromIndexedDB() && period && !firstFetchRef.current) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [period, bundle]);

	useEffect(() => {
		if (bundle) {
			fetchData();
			firstFetchRef.current = true;
		}
	}, [bundle]);

	const fetchData = async (useLoadModal: boolean = true) => {
		setLoading(true);

		const res = await api.get(`/supplier-payments/${bundle ? bundle.date : ""}`, {
			...(!bundle && periodRequestConfig(period)),
		});

		if (res) {
			if (res.status === 200) {
				if (bundle) {
					setData(res.data);
				} else {
					saveFromServer(res.data, period);
				}
				toast.update(toastIdRef.current, {
					render: t("common:toast.success.refresh"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
				toast.dismiss(toastIdRef.current);
			}
		} else {
			toast.dismiss(toastIdRef.current);
		}

		setLoading(false);
	};

	const handleDelete = async () => {
		if (!toDelete) return;
		setLoadingMessage("DELETE");

		const res = await api.delete(`/supplier-payments/${toDelete.id}`);

		if (res.status === 204) {
			toastIdRef.current = toast.loading(
				`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`,
			);
			fetchData();
		} else if (res.status === 409) {
			toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
		} else if (res.status === 401 || res.status === 422) {
			queueReRun(handleDelete);
		}

		setLoadingMessage(null);
		setToDelete(null);
	};

	const handleBulkDelete = async () => {
		if (toDeleteBulk.length === 0) return;
		const payload = { batches: toDeleteBulk };

		setLoadingMessage("DELETE");

		const res = await api.post("/supplier-payments/delete", payload);

		if (res) {
			if (res.status === 204) {
				toastIdRef.current = toast.loading(
					`${t("common:toast.success.delete")} ${t("common:toast.loading.refresh")}`,
				);
				tableRef.current?.resetRowSelection();
				fetchData();
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.delete"), { autoClose: 3000 });
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(handleBulkDelete);
			}
		}

		setLoadingMessage(null);
		setToDeleteBulk([]);
	};

	const columns = useMemo<MRT_ColumnDef<any>[]>(() => [
		{
			header: t("common:tables.date"),
			id: "date",
			accessorFn: (row: any) => parseISODay(row.date),
			Cell: ({ cell }: { cell: any }) => Util.formatDate(cell.getValue(), "medium"),
			filterVariant: "date-range",
		},
		{
			header: t("common:tables.supplier"),
			accessorKey: "supplier",
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.amount"),
			accessorKey: "amount",
			Cell: createSignCell({ signStyle: "accounting" }),
			filterFn: "contains",
			columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
			aggregationFn: "sum",
			AggregatedCell: ({ cell, table }) => (
				<>
					{t("common:tableView.sumBy")}{" "}
					{table.getColumn(cell.row.groupingColumnId ?? "").columnDef.header}:{" "}
					<Box sx={{ color: "info.main", display: "inline", fontWeight: "bold" }}>
						{Util.formatNumber(cell.getValue<number>())}
					</Box>
				</>
			),
			Footer: ({ table }) => {
				const [total, setTotal] = useState<number>(0);

				useEffect(() => {
					const rows = table.getFilteredRowModel().rows;
					const handle = setTimeout(() => {
						let sum = 0;
						for (let i = 0; i < rows.length; i++) {
							const value = Number(rows[i].original.amount);
							if (Number.isFinite(value)) sum += value;
						}
						setTotal(roundQty(sum));
					}, 0);
					return () => clearTimeout(handle);
				}, [table.getFilteredRowModel().rows]);

				return (
					<Box sx={{ fontWeight: "bold", color: "success.main" }}>
						{t("common:report.total")}: {Util.formatNumber(total)}
					</Box>
				);
			},
		},
		{
			header: t("common:tables.mode"),
			accessorKey: "mode",
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:supplySituation.reference"),
			accessorKey: "reference",
			Cell: ({ cell }: { cell: any }) => (
				<button
					type="button"
					style={{
						background: "none",
						border: "none",
						padding: 0,
						color: "inherit",
						cursor: "pointer",
						textDecoration: "underline",
					}}
					onClick={() => setToView({ batch: cell.row.original.batch })}
				>
					{cell.getValue()}
				</button>
			),
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:ui.labels.notes"),
			accessorKey: "description",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.user"),
			accessorKey: "user",
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
		{
			header: t("common:tables.depot"),
			accessorKey: "depot",
			filterVariant: "autocomplete",
			filterFn: "contains",
			columnFilterModeOptions: TEXT_FILTER_OPTIONS,
		},
	], [t]);

	if (hideColumns) {
		hideColumns.forEach((key) => {
			const idx = columns.findIndex((col) => col.accessorKey === key);
			if (idx >= 0) columns.splice(idx, 1);
		});
	}

	const handlePaymentSuccess = () => {
		paymentMade.current = true;
		if (toEdit) {
			toastIdRef.current = toast.loading(
				`${t("common:toast.success.update")} ${t("common:toast.loading.refresh")}`,
			);
		} else {
			toastIdRef.current = toast.loading(
				`${t("common:toast.success.payment")} ${t("common:toast.loading.refresh")}`,
			);
		}
		fetchData();
	};

	return (
		<>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<TableView
				stateKey={bundle ? undefined : LocalStorageKeys.SUPPLY_PAYMENT_RECORDS}
				posting="supply_payment"
					dataScope="supply_payment"
					header={t("common:payments.headers.supplyRecords")}
					data={data}
					columns={columns}
					totals={[{ key: "amount", label: t("common:tables.amount") }]}
					lastUpdated={lastUpdate}
					period={dateRange}
					setDateRangeFilter={bundle ? undefined : setPeriod}
					isLoading={loading}
					bundle={bundle}
					renderTopToolbarActions={(table) => (
						<Box sx={{ display: "flex", gap: "0.5rem" }}>
							<Tooltip title={t("common:ui.tooltips.refreshData")}>
								<IconButton onClick={() => fetchData()} color="primary">
									<Refresh />
								</IconButton>
							</Tooltip>
							{!bundle && (
								<Tooltip title={t("common:ui.tooltips.newPayment")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canRecord(access, 'supply_payment'))) {
												setNewRecord(true);
											}
										}}
										color="success"
									>
										<AddCircleOutline />
									</IconButton>
								</Tooltip>
							)}
							{table.getSelectedRowModel().flatRows.length === 1 && (
								<Tooltip title={t("common:ui.tooltips.editSelectedPayment")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canAmend(access, 'supply_payment'))) {
												setToEdit(table.getSelectedRowModel().flatRows[0].original);
											}
										}}
										color="primary"
									>
										<Edit />
									</IconButton>
								</Tooltip>
							)}
							{table.getSelectedRowModel().flatRows.length > 0 && (
								<Tooltip title={t("common:ui.tooltips.deleteSelection")}>
									<IconButton
										onClick={() => {
											if (allowOrWarn(canDrop(access, 'supply_payment', 'delete_bulk'))) {
												const rowsBatches = [
													...new Set(
														table.getSelectedRowModel().flatRows.map((row: any) => row.original.batch),
													),
												];
												if (rowsBatches.length === 1) {
													setToDelete(table.getSelectedRowModel().flatRows[0].original);
												} else {
													tableRef.current = table;
													setToDeleteBulk(rowsBatches);
												}
											}
										}}
										color="error"
									>
										<Delete />
									</IconButton>
								</Tooltip>
							)}
							<ExportMenuButton
								prefix="supply_payments"
								data={data}
								columns={columns}
								table={table}
							/>
						</Box>
					)}
					renderRowActionMenuItems={(row, closeMenu) => [
						!(toDelete || toEdit) ? (
							<MenuItem
								key="edit"
								onClick={() => {
									if (allowOrWarn(canAmend(access, 'supply_payment'))) {
										setToEdit(row.original);
									}
									closeMenu();
								}}
								sx={{ color: "primary.main" }}
							>
								<ListItemIcon sx={{ color: "primary.main" }}>
									<Edit />
								</ListItemIcon>
								{t("common:ui.buttons.edit")}
							</MenuItem>
						) : null,
						!(toDelete || toEdit) ? (
							<MenuItem
								key="delete"
								onClick={() => {
									if (allowOrWarn(canDrop(access, 'supply_payment'))) {
										setToDelete(row.original);
									}
									closeMenu();
								}}
								sx={{ color: "error.main" }}
							>
								<ListItemIcon sx={{ color: "error.main" }}>
									<Delete />
								</ListItemIcon>
								{t("common:ui.buttons.delete")}
							</MenuItem>
						) : null,
					]}
				/>
			</LocalizationProvider>

			{toView && (
				<SupplyReport
					isOpen={!!toView}
					toggle={() => setToView(null)}
					bundle={toView}
				/>
			)}

			{newRecord && (
				<SupplierBulkPaymentModal
					isOpen={newRecord}
					toggle={() => setNewRecord(false)}
					onSuccess={handlePaymentSuccess}
				/>
			)}

			{toEdit && (
				<SupplierPaymentModal
					isOpen={!!toEdit}
					toggle={() => setToEdit(null)}
					bundle={toEdit}
					editMode
					onSuccess={handlePaymentSuccess}
				/>
			)}

			<ConfirmDialog
				onConfirm={toDelete ? handleDelete : handleBulkDelete}
				onCancel={() => { toDelete ? setToDelete(null) : setToDeleteBulk([]); }}
				isOpen={!!toDelete || toDeleteBulk.length > 0}
				setIsOpen={() => { }}
				title={<><DeleteOutline sx={{ color: "red" }} /> {t("common:dialog.deleteConfirmation")}</>}
				message={
					toDelete ?
						<>
							{t("common:payments.confirm.deleteSupplySingleAmount")}&nbsp;
							<b><u>{Util.formatCurrency(toDelete?.amount, appSettings?.BIZ_CURRENCY?.value || "USD")}</u></b>&nbsp;
							{t("common:payments.confirm.deleteSingleDate")} <b><u>{Util.formatDate(toDelete?.date, "full")}</u></b>&nbsp;
							{t("common:payments.confirm.deleteSupplySingleSupplier")} <b><u>{toDelete?.supplier}</u></b>!
						</>
						:
						<>
							{t("common:payments.confirm.deleteSupplyBulk", { count: Number(toDeleteBulk.length) })}
						</>
				}
				confirmBtnColor="error"
				confirmBtnVariant="contained"
				confirmBtnText={<>{t("common:ui.buttons.delete")}</>}
			/>
		</>
	);
};

export default SupplyPayment;